import { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Menu, MenuItem,
  Avatar, Divider, ListItemIcon, Chip, Tooltip, Drawer, List, ListItemButton,
  ListItemText } from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import MenuIcon      from '@mui/icons-material/Menu';
import SettingsIcon  from '@mui/icons-material/Settings';
import LogoutIcon    from '@mui/icons-material/Logout';
import BoltIcon      from '@mui/icons-material/Bolt';
import FolderIcon    from '@mui/icons-material/Folder';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DarkModeIcon  from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useAuth }   from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { colors } from '../colors';
import { MONTHLY_LIMIT } from '../constants';
import NotificationBell from './NotificationBell';

// Homepage sections, shared by the desktop bar and the mobile drawer.
const PUBLIC_NAV_ANCHORS = [
  { label: 'Pourquoi déclarer', id: 'home-sacem' },
  { label: 'Estimation', id: 'home-royalties' },
  { label: 'Tarifs', id: 'home-pricing' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { mode, toggleTheme, colors: c } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchor, setAnchor] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isPublicNavPage = ['/', '/login', '/register', '/contact', '/mentions-legales', '/politique-confidentialite', '/conditions-utilisation', '/cgv'].includes(location.pathname);

  // Close menu and mobile drawer on route change
  useEffect(() => { setAnchor(null); setMobileOpen(false); }, [location.pathname]);

  const handleHomeAnchorClick = (sectionId) => {
    if (location.pathname === '/') {
      const node = document.getElementById(sectionId);
      if (!node) return;
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    navigate(`/#${sectionId}`);
  };

  // Anchor scrolling only works once the drawer stops covering the page.
  const handleDrawerAnchorClick = (sectionId) => {
    setMobileOpen(false);
    handleHomeAnchorClick(sectionId);
  };

  const handleLogout = async () => { setAnchor(null); await logout(); navigate('/'); };

  // Hide navbar on email-flow pages where the navbar would be a distraction.
  // Login/register keep the navbar so users can switch theme and head back home.
  if (['/check-email', '/verify-email', '/forgot-password', '/reset-password'].includes(location.pathname)) return null;

  const navBg = c.bgPaper;
  const navSx = {
    bgcolor: navBg,
    boxShadow: c.shadowMd,
    borderBottom: `1px solid ${c.border}`,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  };

  return (
    <AppBar
      position="static"
      sx={navSx}
    >
      <Toolbar sx={{ gap: 1 }}>
        {/* Brand */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          <Typography 
            variant="h6" 
            component={Link}
            to="/"
            sx={{
              background: 'linear-gradient(135deg, #38BDF8, #818CF8, #A855F7)',
              WebkitBackgroundClip: 'text', 
              WebkitTextFillColor: 'transparent',
              fontWeight: 800, 
              letterSpacing: '-0.5px', 
              fontSize: '1.3rem',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            Publy Music
          </Typography>
        </Box>

        {!user && isPublicNavPage && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, alignItems: 'center', mr: 1 }}>
              {PUBLIC_NAV_ANCHORS.map((item) => (
                <Button
                  key={item.id}
                  onClick={() => handleHomeAnchorClick(item.id)}
                  sx={{
                    color: colors.textSecondary,
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': { color: colors.textPrimary, bgcolor: colors.border },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
            <Tooltip title={mode === 'dark' ? 'Mode clair' : 'Mode sombre'}>
              <IconButton
                onClick={toggleTheme}
                // Explicit: Tooltip only auto-labels string titles, and the name must outlive it.
                aria-label={mode === 'dark' ? 'Mode clair' : 'Mode sombre'}
                sx={{ display: { xs: 'none', md: 'inline-flex' }, color: colors.textSecondary }}
              >
                {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            {location.pathname !== '/login' && (
              <Button
                component={Link}
                to="/login"
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  color: colors.textSecondary,
                  fontWeight: 500,
                  '&:hover': { color: colors.textPrimary, bgcolor: colors.border }
                }}
              >
                Se connecter
              </Button>
            )}
            {location.pathname !== '/register' && (
              <Button
                component={Link}
                to="/register"
                variant="contained"
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  bgcolor: colors.primaryLight,
                  fontWeight: 600,
                  '&:hover': { bgcolor: colors.primaryLight }
                }}
              >
                S'inscrire
              </Button>
            )}

            <IconButton
              onClick={() => setMobileOpen(true)}
              aria-label="Ouvrir le menu"
              sx={{ display: { xs: 'inline-flex', md: 'none' }, color: colors.textSecondary }}
            >
              <MenuIcon />
            </IconButton>

            <Drawer
              anchor="right"
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              sx={{ display: { xs: 'block', md: 'none' } }}
              PaperProps={{
                sx: { width: 280, bgcolor: c.bgPaper, borderLeft: `1px solid ${c.border}`, p: 1 },
              }}
            >
              <List sx={{ py: 0 }}>
                {PUBLIC_NAV_ANCHORS.map((item) => (
                  <ListItemButton
                    key={item.id}
                    onClick={() => handleDrawerAnchorClick(item.id)}
                    sx={{
                      borderRadius: 2,
                      color: colors.textSecondary,
                      '&:hover': { color: colors.textPrimary, bgcolor: colors.border },
                    }}
                  >
                    <ListItemText primaryTypographyProps={{ fontWeight: 500 }} primary={item.label} />
                  </ListItemButton>
                ))}
              </List>

              <Divider sx={{ my: 1, borderColor: colors.border }} />

              <List sx={{ py: 0 }}>
                <ListItemButton
                  onClick={toggleTheme}
                  sx={{
                    borderRadius: 2,
                    color: colors.textSecondary,
                    '&:hover': { color: colors.textPrimary, bgcolor: colors.border },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {mode === 'dark'
                      ? <LightModeIcon fontSize="small" sx={{ color: colors.warningLight }} />
                      : <DarkModeIcon fontSize="small" sx={{ color: colors.textSecondary }} />}
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontWeight: 500 }}
                    primary={mode === 'dark' ? 'Mode clair' : 'Mode sombre'}
                  />
                </ListItemButton>
              </List>

              <Divider sx={{ my: 1, borderColor: colors.border }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                {location.pathname !== '/login' && (
                  <Button
                    component={Link}
                    to="/login"
                    fullWidth
                    variant="outlined"
                    onClick={() => setMobileOpen(false)}
                    sx={{
                      color: colors.textSecondary,
                      borderColor: colors.border,
                      fontWeight: 500,
                      '&:hover': { color: colors.textPrimary, borderColor: colors.primaryLight },
                    }}
                  >
                    Se connecter
                  </Button>
                )}
                {location.pathname !== '/register' && (
                  <Button
                    component={Link}
                    to="/register"
                    fullWidth
                    variant="contained"
                    onClick={() => setMobileOpen(false)}
                    sx={{
                      bgcolor: colors.primaryLight,
                      fontWeight: 600,
                      '&:hover': { bgcolor: colors.primaryLight },
                    }}
                  >
                    S'inscrire
                  </Button>
                )}
              </Box>
            </Drawer>
          </Box>
        )}

        {user && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              component={Link} to="/dashboard"
              sx={{ color: colors.textSecondary, fontWeight: 500, display: { xs: 'none', sm: 'inline-flex' },
                '&:hover': { color: colors.textPrimary, bgcolor: colors.border } }}
            >
              Tableau de bord
            </Button>
            <Button
              component={Link} to="/documents"
              sx={{ color: colors.textSecondary, fontWeight: 500, display: { xs: 'none', sm: 'inline-flex' },
                '&:hover': { color: colors.textPrimary, bgcolor: colors.border } }}
            >
              Mes déclarations
            </Button>
            <Button
              component={Link} to="/support"
              sx={{ color: colors.textSecondary, fontWeight: 500, display: { xs: 'none', sm: 'inline-flex' },
                '&:hover': { color: colors.textPrimary, bgcolor: colors.border } }}
            >
              Support
            </Button>

            {/* Folder icon visible on mobile only */}
            <Tooltip title="Tableau de bord">
              <IconButton component={Link} to="/dashboard" aria-label="Tableau de bord"
                sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: colors.textSecondary }}>
                <DashboardIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Mes déclarations">
              <IconButton component={Link} to="/documents" aria-label="Mes déclarations"
                sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: colors.textSecondary }}>
                <FolderIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Support">
              <IconButton component={Link} to="/support" aria-label="Support"
                sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: colors.textSecondary }}>
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>

            {(() => {
              const sub = user.subscription;
              if (sub) {
                const used      = sub.declarations_used_this_month;
                const monthlyLimit = sub.monthly_limit ?? MONTHLY_LIMIT;
                const remaining = monthlyLimit - used;
                const subOk     = remaining > 0;
                return (
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Tooltip title={`Abonnement mensuel — ${used}/${monthlyLimit} déclarations utilisées`}>
                      <Chip
                        icon={<BoltIcon />}
                        label={`${remaining} /mois`}
                        onClick={() => navigate('/settings#credits')}
                        size="small"
                        sx={{
                          bgcolor: subOk ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)',
                          color:   subOk ? colors.success : colors.errorLight,
                          fontWeight: 600, cursor: 'pointer',
                          border: `1px solid ${subOk ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                          '& .MuiChip-icon': { color: subOk ? colors.success : colors.error },
                          '&:hover': { bgcolor: subOk ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)' },
                        }}
                      />
                    </Tooltip>
                    {user.credits > 0 && (
                      <Tooltip title={`${user.credits} crédit${user.credits !== 1 ? 's' : ''} unitaire${user.credits !== 1 ? 's' : ''} — utilisés en fallback si limite mensuelle atteinte`}>
                        <Chip
                          icon={<BoltIcon />}
                          label={`+${user.credits}`}
                          onClick={() => navigate('/settings#credits')}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(79,70,229,0.25)',
                            color: colors.primaryLight,
                            fontWeight: 600, cursor: 'pointer',
                            border: '1px solid rgba(129,140,248,0.3)',
                            '& .MuiChip-icon': { color: colors.warning },
                            '&:hover': { bgcolor: 'rgba(79,70,229,0.4)' },
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                );
              }
              const ok = user.credits > 0;
              return (
                <Tooltip title="Crédits restants — cliquez pour recharger">
                  <Chip
                    icon={<BoltIcon />}
                    label={`${user.credits} crédit${user.credits !== 1 ? 's' : ''}`}
                    onClick={() => navigate('/settings#credits')}
                    sx={{
                      bgcolor: ok ? 'rgba(79,70,229,0.25)' : 'rgba(239,68,68,0.2)',
                      color:   ok ? colors.primaryLight : colors.errorLight,
                      fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${ok ? 'rgba(129,140,248,0.3)' : 'rgba(239,68,68,0.3)'}`,
                      '& .MuiChip-icon': { color: ok ? colors.warning : colors.error },
                      '&:hover': { bgcolor: ok ? 'rgba(79,70,229,0.4)' : 'rgba(239,68,68,0.3)' },
                    }}
                  />
                </Tooltip>
              );
            })()}

            <NotificationBell />

            <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label="Menu utilisateur" sx={{ ml: 0.5 }}>
              <Avatar sx={{
                width: 34, height: 34, fontSize: 14, fontWeight: 700,
                background: 'linear-gradient(135deg, #4F46E5, #A855F7)',
              }}>
                {user.username?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
              </Avatar>
            </IconButton>

            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{ sx: { bgcolor: colors.bgPaper, border: `1px solid ${colors.border}`, minWidth: 200 } }}>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: colors.textPrimary }}>{user.username}</Typography>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>{user.email}</Typography>
              </Box>
              <Divider sx={{ borderColor: colors.border }} />
              <MenuItem onClick={() => { setAnchor(null); toggleTheme(); }}
                sx={{ color: colors.textSecondary, '&:hover': { bgcolor: colors.border } }}>
                <ListItemIcon>
                  {mode === 'dark'
                    ? <LightModeIcon fontSize="small" sx={{ color: colors.warningLight }} />
                    : <DarkModeIcon fontSize="small" sx={{ color: colors.textSecondary }} />}
                </ListItemIcon>
                {mode === 'dark' ? 'Mode clair' : 'Mode sombre'}
              </MenuItem>
              <Divider sx={{ borderColor: colors.border }} />
              <MenuItem onClick={() => { setAnchor(null); navigate('/settings'); }}
                sx={{ color: colors.textSecondary, '&:hover': { bgcolor: colors.border } }}>
                <ListItemIcon><SettingsIcon fontSize="small" sx={{ color: colors.textSecondary }} /></ListItemIcon>
                Paramètres
              </MenuItem>
              <MenuItem onClick={() => { setAnchor(null); navigate('/support'); }}
                sx={{ color: colors.textSecondary, '&:hover': { bgcolor: colors.border } }}>
                <ListItemIcon><HelpOutlineIcon fontSize="small" sx={{ color: colors.textSecondary }} /></ListItemIcon>
                Support
              </MenuItem>
              <MenuItem onClick={handleLogout}
                sx={{ color: colors.errorLight, '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' } }}>
                <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: colors.errorLight }} /></ListItemIcon>
                Déconnexion
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}
